import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const NotificationSystem = () => {
  const { user, getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navigate = useNavigate();
  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user?.id || user?.primaryRole !== 'admin') return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/notifications`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setNotifications(result.data || []);
          setError(null);
        } else {
          setError(result.message || 'Failed to fetch notifications');
        }
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update local state
          setNotifications(prev => 
            prev.map(notif => 
              notif._id === notificationId 
                ? { ...notif, isRead: true }
                : notif
            )
          );
        }
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Clear all notifications (set deletedAt)
  const clearAllNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/notifications/clear-all`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Clear all notifications from local state
          setNotifications([]);
        }
      }
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setDetailDialogOpen(true);
    setDropdownOpen(false); // Close dropdown when opening detail dialog
    
    // Mark as read if not already read
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = () => {
    if (!dropdownOpen) {
      fetchNotifications();
    }
    setDropdownOpen(!dropdownOpen);
  };

  // Handle click away to close dropdown
  const handleClickAway = () => {
    setDropdownOpen(false);
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'access_request':
        return <BusinessIcon color="primary" />;
      case 'user_request':
        return <PersonIcon color="primary" />;
      case 'system_alert':
        return <NotificationsIcon color="warning" />;
      default:
        return <NotificationsIcon color="default" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      
      // Set up polling for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative' }}>
        {/* Notification Bell with Badge */}
        <Tooltip title={`${unreadCount} unread notifications`}>
          <IconButton
            sx={{
              color: 'text.primary',
              width: 35,
              height: 35,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={handleDropdownToggle}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon 
                sx={{
                  animation: unreadCount > 0 ? 'bellShake 2s ease-in-out infinite' : 'none',
                  '@keyframes bellShake': {
                    '0%, 100%': {
                      transform: 'rotate(0deg)',
                    },
                    '10%, 30%, 50%, 70%, 90%': {
                      transform: 'rotate(-10deg)',
                    },
                    '20%, 40%, 60%, 80%': {
                      transform: 'rotate(10deg)',
                    },
                  },
                }}
              />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Notification Dropdown Panel */}
        {dropdownOpen && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              width: 400,
              maxWidth: '90vw',
              maxHeight: '70vh',
              mt: 1,
              zIndex: 1300,
              boxShadow: 3,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Notification
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {notifications.length > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={clearAllNotifications}
                      sx={{ fontSize: '0.75rem', py: 0.5 }}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {error}
                </Alert>
              ) : notifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No notifications yet
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {notifications.map((notification) => (
                    <ListItem
                      key={notification._id}
                      sx={{
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        bgcolor: notification.isRead ? 'white' : 'rgba(255, 0, 0, 0.05)', // Light red for unread
                        alignItems: 'flex-start',
                        py: 2,
                      }}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'grey.700',
                          color: 'white',
                          fontSize: '0.875rem',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {notification.fromUser?.firstName && notification.fromUser?.lastName
                          ? `${notification.fromUser.firstName.charAt(0)}${notification.fromUser.lastName.charAt(0)}`.toUpperCase()
                          : notification.fromUser?.firstName?.charAt(0) || 'U'
                        }
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {notification.fromUser ? 
                            `${notification.fromUser.firstName} ${notification.fromUser.lastName}` : 
                            'Unknown User'
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(notification.createdAt)}
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Footer */}
            {notifications.length > 0 && (
              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                <Button
                  size="small"
                  variant="text"
                  sx={{ fontSize: '0.75rem' }}
                  onClick={() => {
                    setDropdownOpen(false);
                    // You can add a "View All" page navigation here
                  }}
                >
                  View all →
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* Notification Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                color: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: '#f3f4f6'
              }}>
                {getNotificationIcon(selectedNotification?.type)}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151' }}>
                {selectedNotification?.title}
              </Typography>
            </Box>
          </DialogTitle>
          {selectedNotification?.type === 'access_request' && (
          <DialogContent sx={{ px: 3, pt: 1 }}>
            {selectedNotification && (
              <>
                {/* User Information */}
                {selectedNotification.fromUser && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 2 }}>
                      Requested by:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Avatar sx={{ 
                        width: 40, 
                        height: 40, 
                        fontSize: '0.875rem', 
                        flexShrink: 0, 
                        bgcolor: '#6b7280', 
                        color: 'white',
                        fontWeight: 600
                      }}>
                        {selectedNotification.fromUser.firstName && selectedNotification.fromUser.lastName
                          ? `${selectedNotification.fromUser.firstName.charAt(0)}${selectedNotification.fromUser.lastName.charAt(0)}`.toUpperCase()
                          : selectedNotification.fromUser.firstName?.charAt(0) || 'U'
                        }
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151', mb: 0.5 }}>
                          {selectedNotification.fromUser.firstName} {selectedNotification.fromUser.lastName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                          {selectedNotification.fromUser.email}
                        </Typography>
                        {selectedNotification.fromUser.department && (
                          <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5 }}>
                            Department: {selectedNotification.fromUser.department}
                          </Typography>
                        )}
                        {selectedNotification.fromUser.jobTitle && (
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Job Title: {selectedNotification.fromUser.jobTitle}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Request Message */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 1.5 }}>
                    Request Details:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                    {selectedNotification.message}
                  </Typography>
                </Box>
                  
                <Box
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Close the dialog
                    setDetailDialogOpen(false);
                    navigate(`/admin/admin-existing?email=${selectedNotification.fromUser.email}`);
                  }}
                  sx={{
                    color: '#7c3aed',
                    textDecoration: 'underline',
                    textDecorationColor: '#7c3aed',
                    textUnderlineOffset: '3px',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    cursor: 'pointer',
                    display: 'inline-block',
                    padding: '8px 0',
                    '&:hover': {
                      color: '#6d28d9',
                      textDecorationColor: '#6d28d9',
                      textDecorationThickness: '2px',
                    },
                    '&:visited': {
                      color: '#7c3aed',
                    },
                  }}
                >
                  GO TO ACCESS PROVISIONING
                </Box>

     
                {/* Timestamp */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                    {formatDate(selectedNotification.createdAt)}
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
                 )}
          <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
            <Button 
              onClick={() => setDetailDialogOpen(false)}
              sx={{
                color: '#7c3aed',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: '8px 16px',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'rgba(124, 58, 237, 0.04)',
                  color: '#6d28d9',
                },
              }}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ClickAwayListener>
  );
};

export default NotificationSystem;